import { useState } from 'react';
import { Search, Building2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useVehicleRegistry } from './useVehicleRegistry';
import { formatUkTime, formatDwell } from './historicalDataFormat';
import { VehiclePickerDropdown } from './VehiclePickerDropdown';

const todayStr = () => new Date().toISOString().slice(0, 10);

const BranchBadge = ({ branchId }) => branchId ? (
  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${branchId === 'E70' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>{branchId}</span>
) : null;

/** Multi-select vehicle + postcode + radius + date-range search: did any of
 * these vehicles visit this postcode, and when (arrived/left + total dwell)? */
export const PostcodeVisitSearch = () => {
  const { vehicles, loading: loadingVehicles } = useVehicleRegistry();
  const [selected, setSelected] = useState(new Set());
  const [postcode, setPostcode] = useState('');
  const [radiusMiles, setRadiusMiles] = useState('0.3');
  const [dateFrom, setDateFrom] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const search = async () => {
    if (selected.size === 0) { toast.error('Select at least one vehicle'); return; }
    if (!postcode.trim()) { toast.error('Enter a postcode'); return; }
    setSearching(true);
    setResults(null);
    try {
      const payload = {
        vehicles: vehicles.filter((v) => selected.has(v.samsara_vehicle_id)).map((v) => ({
          samsara_vehicle_id: v.samsara_vehicle_id, vehicle_registration: v.vehicle_registration, branch_id: v.branch_id,
        })),
        postcode: postcode.trim(), date_from: dateFrom, date_to: dateTo,
        radius_miles: parseFloat(radiusMiles) || 0.3,
      };
      const res = await api.post(`${API}/transport/samsara/postcode-visit-search`, payload);
      setResults(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="postcode-visit-search">
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
        <div>
          <Label>Vehicles</Label>
          <div className="mt-1.5">
            {loadingVehicles ? (
              <div className="text-xs text-gray-400 py-2">Loading Samsara fleet…</div>
            ) : (
              <VehiclePickerDropdown vehicles={vehicles} selected={selected} onChange={setSelected} />
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="hdc-postcode">Postcode</Label>
            <Input id="hdc-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="e.g. B1 1AA" className="bg-white" data-testid="hdc-postcode-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hdc-radius">Radius (miles)</Label>
            <Input id="hdc-radius" type="number" step="0.1" min="0.05" value={radiusMiles} onChange={(e) => setRadiusMiles(e.target.value)} className="bg-white" data-testid="hdc-radius-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hdc-date-from">From</Label>
            <Input id="hdc-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white" data-testid="hdc-date-from" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="hdc-date-to">To</Label>
            <Input id="hdc-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white" data-testid="hdc-date-to" />
          </div>
        </div>
        <Button onClick={search} disabled={searching} className="text-white gap-2" style={{ backgroundColor: '#ee7203' }} data-testid="hdc-search-btn">
          <Search className="w-4 h-4" /> {searching ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {results && (
        <div className="space-y-3" data-testid="hdc-results">
          <div className="text-xs text-gray-500">
            Results for <span className="font-bold text-gray-700">{results.postcode}</span> between {results.date_from} and {results.date_to}
          </div>
          {results.results.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-8">No vehicles matched — try widening the date range, radius, or vehicle selection.</div>
          ) : (
            results.results.map((r) => (
              <div key={r.vehicle_registration} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`hdc-result-${r.vehicle_registration}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 font-bold text-slate-900">
                    <Building2 className="w-4 h-4 text-gray-400" /> {r.vehicle_registration}
                    <BranchBadge branchId={r.branch_id} />
                  </div>
                  <div className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {r.visit_count} visit{r.visit_count === 1 ? '' : 's'} · {formatDwell(r.total_dwell_minutes)} total
                  </div>
                </div>
                {r.visits.length === 0 ? (
                  <div className="text-xs text-gray-400">No visits found in this date range</div>
                ) : (
                  <div className="space-y-1">
                    {r.visits.map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`hdc-visit-${r.vehicle_registration}-${i}`}>
                        <span className="text-gray-600">{formatUkTime(v.arrived_at)} → {formatUkTime(v.left_at)}</span>
                        <span className="font-bold text-orange-600">{formatDwell(v.dwell_minutes)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PostcodeVisitSearch;
